import React, {useState, useEffect, useContext} from 'react'
import Plot from 'react-plotly.js'
import {Select} from 'antd'
import axios from 'axios'
import {Card, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {useSelectedWeek} from '../../../../../../contexts/SelectedWeekContext'

interface DataItem {
  week: string
  ratioAll: number
  geography: string
  countAll: number
}
interface Trace {
  x: string[]
  y: number[]
  type: string
  mode: string
  marker?: {color: string}
  line?: {color: string}
  yaxis?: string
  name: string
}
interface DataTrend {
  week: string
  geography: string
  trend: number
}
const {Option} = Select

const StreamgraphChart: React.FC = () => {
  const {selectedWeek} = useSelectedWeek('2023-W40')
  const [data, setData] = useState<DataItem[]>([])
  const [datat, setDatat] = useState<DataTrend[]>([])
  const [selectedAgeClass, setSelectedAgeClass] = useState<string[]>(['0-200'])
  const [ageClass, setAgeClass] = useState<string[]>([
    '<1',
    '01-avr',
    'mai-14',
    '15-34',
    '35-59',
    '60-79',
    '80+',
    '0-200',
  ])
  const [missingData, setMissingData] = useState<DataItem[]>([])

  const [RegionClass, setRegionClass] = useState([])
  const [selectedRegionClass, setSelectedRegionClass] = useState<string[]>(['DE'])
  const [plotData, setPlotData] = useState<Trace[]>([])
  const API_URL = process.env.REACT_APP_API_URL
  function addMissingWeek(data: DataItem[]): DataItem[] {
    const startWeek = '2023-W34' // First week in the data
    const endWeek = '2024-W18' // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const newData: DataItem[] = []

    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          ratioAll: 0,
          geography: '',
          countAll: 0,
        })
        missingData.push({
          week: currentWeek,
          ratioAll: 0,
          geography: '',
          countAll: 0,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week <= '2023-W52') {
        insertionIndex = i + 1
      } else {
        break
      }
    }

    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }
  const parseAgeClasses = (selectedAgeClass: string[]): string => {
    return selectedAgeClass.join(',')
  }
  const traces: Trace[] = []
  const fetchData = async (ageClasses = selectedAgeClass, geo = selectedRegionClass) => {
    try {
      // Fetch data for each age class and generate traces
      await Promise.all(
        ageClasses.map(async (ageClass, index) => {
          // Pass index to get color from colors array
          let filterAge = parseAgeClasses([ageClass])
          let response = await axios.get(
            API_URL +
              `/consultationNumberRespiratorySyndrome/list?ages=${filterAge}&geography=${geo}`
          )

          let readableAge
          let selectedColor
          switch (ageClass) {
            case '<1':
              readableAge = 'Less than 1 years'
              selectedColor = '#FF2F00'
              break
            case '01-avr':
              readableAge = '1 to 4 years'
              selectedColor = '#33FF57'
              break
            case 'mai-14':
              readableAge = '5 to 14 years'
              selectedColor = '#5733FF'
              break
            case '15-34':
              readableAge = '15 to 34 years'
              selectedColor = '#FF33DD'
              break
            case '35-59':
              readableAge = '35 to 59 years'
              selectedColor = '#33DDFF'
              break
            case '60-79':
              readableAge = '60 to 79 years'
              selectedColor = '#1E778D'
              break
            case '80+':
              readableAge = '80 and older'
              selectedColor = '#4B322C'
              break
            default:
              readableAge = 'All ages'
              selectedColor = '#203879'
              break
          }

          const trace: Trace = {
            x: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
              })
              .map((entry) => entry.week),
            y: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
              })
              .map((entry) => entry.ratioAll),
            yaxis: 'y2',
            type: 'scatter',
            mode: 'lines',
            line: {color: selectedColor}, // Assign color based on index
            name: `${readableAge}`, // Add age class to trace name
          }
          traces.push(trace)
        })
      )
      // const missdata = {
      //   x: missingData.map((entry) => entry.week),
      //   y: missingData.map((entry) => entry.ratioAll),
      //   type: 'scatter',
      //   mode: 'markers',
      //   yaxis: 'y2',
      //   name: 'Missing Data',
      //   marker: {color: '#000000'},
      // }
      // traces.push(missdata)
      const response = await axios.get(
        API_URL + `/consultationNumberRespiratorySyndrome/list?ages=0-200&geography=${geo}`
      )
      const trace0 = {
        x: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
          })
          .map((entry) => entry.week),
        y: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
          })
          .map((entry) => entry.countAll),
        yaxis: 'y',
        type: 'bar',
        mode: '',
        marker: {color: '#b0dff9'}, // Assign color based on index
        name: `Total number of doctor visits`, // Add age class to trace name
      }
      traces.push(trace0)
      setPlotData(traces)
      //console.log(response.data);
    } catch (error) {
      console.error('Error fetching data:', error)
      setData([])
    }
  }

  // const fetchData3 = async (ageClasses = selectedAgeClass, geo = selectedRegionClass) => {
  //   try {
  //     let filterAge = parseAgeClasses(ageClasses)

  //     //const filterRegion = parseRegion(geography)
  //     const response = await axios.get(
  //       API_URL + `/consultationNumberRespiratorySyndrome/list?ages=${filterAge}&geography=${geo}`
  //     )
  //     setData(response.data)

  //     const trace0 = {
  //       x: addMissingWeek(response.data)
  //         .filter((entry) => {
  //           const currentYear = parseInt(entry.week.substring(0, 4), 10)
  //           const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
  //           return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
  //         })
  //         .map((entry) => entry.week),
  //       y: addMissingWeek(response.data)
  //         .filter((entry) => {
  //           const currentYear = parseInt(entry.week.substring(0, 4), 10)
  //           const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
  //           return (currentYear === 2023 && currentWeek >= 35) || currentYear > 2023
  //         })
  //         .map((entry) => entry.countAll),
  //       yaxis: 'y',
  //       type: 'bar',
  //       mode: '',
  //       marker: {color: '#b0dff9'}, // Assign color based on index
  //       name: `Total number of doctor visits`, // Add age class to trace name
  //     }
  //     traces.push(trace0)
  //   } catch (error) {
  //     console.error('Error fetching data:', error)
  //     // Handle error or set a default value for data
  //     setData([])
  //   }
  // }

  useEffect(() => {
    // const fetchData2 = async (geo = selectedRegionClass) => {
    //   try {
    //     const response = await axios.get(
    //       `${API_URL}/consultationNumberRespiratorySyndromeTrend/trend?geography=${geo}&week=${selectedWeek}`
    //     )
    //     setDatat(response.data)
    //   } catch (error) {
    //     console.error('Error fetching data:', error)
    //     // Handle error or set a default value for data
    //     setData([])
    //   }
    // }
    const listRegionClass = async () => {
      try {
        const response = await axios.get(
          API_URL + '/consultationNumberRespiratorySyndrome/listGeography'
        )
        const data = response.data
        setRegionClass(data)
      } catch (error) {
        console.error('Error fetching data:', error)
        return null
      }
    }
    listRegionClass()
    setSelectedAgeClass(selectedAgeClass)
    fetchData(selectedAgeClass, selectedRegionClass)
    // fetchData2(selectedRegionClass)
  }, [selectedAgeClass, selectedRegionClass, selectedWeek])

  // const trace2 = {
  //   x: data.map((entry) => entry.week),
  //   y: data.map((entry) => entry.min),
  //   type: 'scatter',
  //   mode: 'lines',
  //   fill: 'tonexty',
  //   marker: {color: '#DBDFE9'},
  //   name: 'min',
  // }
  //   const trace3 = {
  //     x: data.map((entry) => entry.week),
  //     y: data.map((entry) => entry.max),
  //     type: 'scatter',
  //     marker: {color: '#DBDFE9'},
  //     name: 'max',
  //   }

  // const trace4 = {
  //   x: data.map((entry) => entry.week ),
  //   y: data.map((entry) => entry.value ),
  //   yaxis: 'y',
  //   type: 'scatter',
  //   mode: 'lines',
  //   line: { color: '#203879' },
  //   name: '#Visits for respiratory syndromes',
  // }
  // const plotData = [trace4]

  const selectedYear = parseInt(selectedWeek.slice(0, 4))
  const selectedWeekNumber = parseInt(selectedWeek.slice(6))
  const startDate = 2023
  const startWeek = 35
  const endYear = 2024
  const endWeek = 18
  const showArrow =
    (selectedYear > startDate || (selectedYear === startDate && selectedWeekNumber >= startWeek)) &&
    (selectedYear < endYear || (selectedYear === endYear && selectedWeekNumber <= endWeek))
  const layout = {
    title: '% of doctor visits for respiratory syndromes among all doctor visits',
    barmode: 'stack',
    bargap: 0.2,
    bargroupgap: 0.1,
    autosize: true,
    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: -0.035,
        xanchor: 'right',
        y: 0.5,
        yanchor: 'bottom',
        text: '#',
        font: {
          size: 25,
          color: '#b0dff9',
        },
        showarrow: false,
      },
      {
        x: selectedWeek,
        y: 0,
        xref: 'x',
        yref: 'y',
        text: showArrow ? 'Selected Week' : '',
        font: {
          family: 'Courier New, monospace',
          size: 16,
          color: '#CC1B1B',
        },
        arrowcolor: '#CC1B1B',
        showarrow: showArrow,
        arrowhead: 7,
        ax: 0,
        ay: -240,
      },
      {
        xref: 'paper',
        yref: 'paper',
        x: 1.02,
        xanchor: 'left',
        y: 0.5,
        yanchor: 'bottom',
        text: '%',
        font: {
          size: 25,
          //color: '#203879',
        },
        showarrow: false,
      },
    ],
    xaxis: {
      title: 'Weeks',
      autorange: true,
      type: 'category',
      tickangle: -45,
    },
    yaxis: {
      autorange: true,
    },
    yaxis2: {
      showgrid: false,
      // titlefont: {color: '#203879'},
      // tickfont: {color: '#203879'},
      overlaying: 'y',
      side: 'right',
      zeroline: false,
    },
    legend: {
      x: 0,
      y: -0.5,
      orientation: 'h',
    },
  }

  function handleDataChangeAge(value): void {
    if (value.length === 0) {
      setSelectedAgeClass(['0-200'])
    } else {
      setSelectedAgeClass(value)
    }
  }

  function handleDataChangeRegion(value): void {
    setSelectedRegionClass(value)
  }

  return (
    <>
      <div className='mx-4'>
        {/* begin::Row  */}
        <div className='row '>
          <Card className='mb-8'>
            <div className='card'>
              <div className='card-body'>
                <h2 className='text-primary bold'>
                  {' '}
                  Doctor visits for respiratory syndromes (Germany){' '}
                  <OverlayTrigger
                    placement='right'
                    overlay={
                      <Tooltip id='logo-tooltip' style={{width: '300px'}}>
                        <p>
                          The doctor visits data in Germany is based on a research panel of
                          anonymized health data sharing GP practices (approx. 2.000 participating
                          doctors). Relevant diagnoses for respiratory diseases (number of diagnoses
                          per ICD-code associated with respiratory syndromes) are processed based on
                          the partnering with CompuGroup Medical and its health data trust center
                          docmetric.
                        </p>
                      </Tooltip>
                    }
                  >
                    <img
                      style={{width: '12px', marginLeft: '10px'}}
                      alt='logo'
                      src={toAbsoluteUrl('/media/logos/ii.svg')}
                    />
                  </OverlayTrigger>
                </h2>
              </div>
              <Card.Body className='row' style={{marginTop: '-35px'}}>
                <div className='row'>
                  <label style={{width: '10%'}} className='col-1 mt-3 text-primary fw-medium fs-5'>
                    Age group :
                  </label>
                  <div className='col-6'>
                    <Select
                      variant='borderless'
                      className='border border-gray-500 border-3 rounded'
                      style={{width: '71%'}}
                      size='large'
                      value={selectedAgeClass}
                      onChange={(value) => handleDataChangeAge(value)}
                      mode='multiple'
                    >
                      {ageClass.map((category) => {
                        let readableAge
                        switch (category) {
                          case '<1':
                            readableAge = 'Less than 1 years'
                            break

                          case 'mai-14':
                            readableAge = '5 to 14 years'
                            break
                          case '01-avr':
                            readableAge = '1 to 4 years'
                            break
                          case '15-34':
                            readableAge = '15 to 34 years'
                            break
                          case '35-59':
                            readableAge = '35 to 59 years'
                            break
                          case '60-79':
                            readableAge = '60 to 79 years'
                            break
                          case '80+':
                            readableAge = '80 and older'
                            break
                          case '0-200':
                            readableAge = 'All ages'
                            break
                        }
                        return (
                          <Option key={category} value={category}>
                            {readableAge}
                          </Option>
                        )
                      })}
                    </Select>
                  </div>
                  <div className='col-2'></div>
                  {/* <div className='col-2'>
                    <div style={{textAlign: 'right'}}>
                      {datat.map((item) => (
                        <div className='text-primary fw-medium fs-6'>
                          Trend (past 5 weeks) :{' '}
                          <div
                            className='border border-gray-500 border-3 rounded'
                            style={{
                              border: 'border border-gray-500 border-3 rounded',
                              display: 'inline-block', // Assure que le div n'occupe que l'espace nécessaire
                              padding: '6px', // Espace à l'intérieur du cadre
                            }}
                          >
                            <i
                              style={{
                                color:
                                  item.trend === null
                                    ? 'transparent'
                                    : Number(item.trend) > 45
                                    ? 'red'
                                    : item.trend > 0
                                    ? 'orange'
                                    : 'green',
                                fontSize: '20px',
                                transform: `rotate(${-1 * item.trend}deg)`,
                              }}
                              className='fa-solid fa-arrow-right'
                            ></i>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div> */}
                </div>
                <div className='row'>
                  <label style={{width: '10%'}} className='col-1 mt-3 text-primary fw-medium fs-5'>
                    Region :
                  </label>
                  <div className='col-6'>
                    <Select
                      variant='borderless'
                      className='border border-gray-500 border-3 rounded'
                      style={{width: '71%'}}
                      size='large'
                      value={selectedRegionClass}
                      onChange={(value) => handleDataChangeRegion(value)}
                      //mode='multiple'
                    >
                      {RegionClass.map((category) => {
                        let readableAge
                        switch (category) {
                          case 'DE':
                            readableAge = 'All regions'
                            break
                          case 'DE.BW':
                            readableAge = 'Baden-Württemberg'
                            break
                          case 'DE.BY':
                            readableAge = 'Bayern'
                            break
                          case 'DE.BE':
                            readableAge = 'Berlin'
                            break
                          case 'DE.BB':
                            readableAge = 'Brandenburg'
                            break
                          case 'DE.HB':
                            readableAge = 'Bremen'
                            break
                          case 'DE.HH':
                            readableAge = 'Hamburg'
                            break
                          case 'DE.HE':
                            readableAge = 'Hessen'
                            break
                          case 'DE.MV':
                            readableAge = 'Mecklenburg-Vorpommern'
                            break
                          case 'DE.NI':
                            readableAge = 'Niedersachsen'
                            break
                          case 'DE.NW':
                            readableAge = 'Nordrhein-Westfalen'
                            break
                          case 'DE.RP':
                            readableAge = 'Rheinland-Pfalz'
                            break
                          case 'DE.SL':
                            readableAge = 'Saarland'
                            break
                          case 'DE.SN':
                            readableAge = 'Sachsen'
                            break
                          case 'DE.ST':
                            readableAge = 'Sachsen-Anhalt'
                            break
                          case 'DE.SH':
                            readableAge = 'Schleswig-Holstein'
                            break
                          case 'DE.TH':
                            readableAge = 'Thüringen'
                            break
                          default:
                            readableAge = 'Unknown'
                            break
                        }
                        return (
                          <Option key={category} value={category}>
                            {readableAge}
                          </Option>
                        )
                      })}
                      {/* <Option key={'all'} value={'all'}>
              {'all regions'}
            </Option> */}
                    </Select>
                  </div>
                </div>
                <Plot
                  style={{width: '100%', height: '500px'}}
                  data={plotData}
                  layout={layout}
                  config={{
                    displaylogo: false,
                    responsive: true,
                  }}
                />

                <p className='card-text source mt-2'>
                  <i>
                    Source:
                    <a className='fw-bold' href='https://www.cgm.com/en'>
                      {' '}
                      CGM/Docmetric{' '}
                    </a>
                  </i>{' '}
                </p>
              </Card.Body>
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}

export default StreamgraphChart
